<template>
  <div>
    <TitleBanner title="Select Site" />
    <div v-if="showBypassOption" class="mt-4 px-4">
      <PrimaryButton
        action="I'M NOT ON SITE (BYPASS)"
        class="bypass"
        type="button"
        @click.once="bypass"
      />
    </div>
    <section
      v-if="sites.length || loading"
      class="card flow-root divide-y divide-gray-200"
    >
      <div v-for="site in sites" :key="site.id" class="">
        <button
          class="w-full px-2 py-4 text-left"
          type="button"
          @click="selectSite(site)"
        >
          <SiteListItem
            :customer-logo="getCustomerLogo(site.customer)"
            :site-address="site.street"
            :site-name="site.name"
          />
        </button>
      </div>
    </section>
    <section v-else class="card">
      <p class="py-4 text-center">No sites found within {{ radius }} meters</p>
    </section>
    <LoadingView :is-loading="loading" :message="loadingMessage" />
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent, onMounted, ref } from 'vue'
  import TitleBanner from '@/components/TitleBanner.vue'
  import { Customer, getNearbySites, Site } from '@/services/api/site'
  import { useRouter } from 'vue-router'
  import config from '@/config'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import SiteListItem from '@/components/SiteListItem.vue'
  import { resetStoreExceptUser } from '@/components/use/resetStore'
  import InvalidStoreException from '@/exceptions/InvalidStoreException'
  import Log from '@/utilities/Log'
  import customerLogoMap from '@/utilities/customerLogo'
  import { getPlate } from '@/services/api/plate'
  import { getCurrentEventRoute } from '@/utilities/session'
  import handleError from '@/utilities/handleError'
  import LoadingView from '@/views/LoadingView.vue'
  import { useUserLogin } from '@/storage/userLogin'
  import { storeToRefs } from 'pinia'
  import { useCustomerStore } from '@/storage/customer'
  import { useNativeScanStore } from '@/storage/nativeScan'
  import { useUserPositionStore } from '@/storage/userPosition'
  import { useSiteStore } from '@/storage/site'
  import { useUserStore } from '@/storage/user'
  import RecoverableException from '@/exceptions/RecoverableException'

  export default defineComponent({
    name: 'SelectNearbySite',
    components: {
      LoadingView,
      PrimaryButton,
      SiteListItem,
      TitleBanner,
    },

    async setup() {
      const router = useRouter()
      const { setSite, setSelectionConfirmed, resetSite } = useSiteStore()
      const { name: siteName, id: siteId } = storeToRefs(useSiteStore())
      const {
        id: userId,
        isUserStoreDefault,
        activeVendorBrandIds,
        contractorCompanies,
      } = storeToRefs(useUserStore())
      const { position, userHasLocationAndIsNotOnSite } = storeToRefs(
        useUserPositionStore(),
      )
      const { setCustomerInfo, resetCustomer } = useCustomerStore()
      const { isVisitor } = storeToRefs(useUserLogin())
      const { setUuid, resetNativeScan } = useNativeScanStore()
      const { uuid: plateUuid } = storeToRefs(useNativeScanStore())
      const sites = ref<Site[]>([])
      const loading = ref(false)
      const noResult = computed(() => {
        return sites.value.length === 0 && !loading.value
      })
      const loadingMessage = ref('Loading...')
      const radius = config.distance.radius.user
      const showBypassOption = computed(() => {
        return (
          contractorCompanies.value.some((company) => !company.deletedAt) &&
          !isVisitor.value
        )
      })

      onMounted(() => {
        setSelectionConfirmed(false)
      })

      let isLoaded = false

      if (plateUuid.value?.length === 36) {
        loadingMessage.value = 'Loading site...'
        isLoaded = await loadSiteByPlate(plateUuid.value)
      }
      if (!isLoaded) {
        loadingMessage.value = 'Loading sites...'
        await loadSites()
      }

      async function bypass(): Promise<void> {
        await router.replace({ name: 'bypass' })
      }

      async function loadSiteByPlate(uuid: string): Promise<boolean> {
        try {
          const plate = await getPlate(uuid)

          if (plate.site.id === siteId.value) {
            await router.replace(getCurrentEventRoute())
            return true
          }

          Log.storeResetExcludingUser('selectNearbySite.ts', 'getPlate')
          resetStoreExceptUser()
          // reinstate uuid as required to confirm site
          setUuid(uuid)
          setSite(plate.site)
          setCustomerInfo(plate.site.customer)

          if (userHasLocationAndIsNotOnSite.value) {
            await router.replace({ name: 'notOnSite' })
            return true
          }

          await router.replace({ name: 'confirmNearbySite' })

          return true
        } catch (error) {
          await handlePlateNotFoundError(error)

          return false
        }
      }

      async function handlePlateNotFoundError(error: Error | unknown) {
        resetSite()
        resetCustomer()
        resetNativeScan()

        Log.siteStoreReset('selectNearbySite.ts', 'getPlate')
        Log.customerStoreReset('selectNearbySite.ts', 'getPlate')

        await handleError(
          new RecoverableException(
            (error as Error).message ?? 'An unknown error occurred.',
          ),
        )
      }

      async function loadSites(): Promise<void> {
        loading.value = true

        if (isUserStoreDefault.value) {
          throw new InvalidStoreException()
        }

        const response = await getNearbySites(
          userId.value.toString(),
          position.value,
          radius,
          activeVendorBrandIds.value,
        )

        if (response) {
          sites.value = response
        }
        loading.value = false
      }

      async function selectSite(site: Site): Promise<void> {
        setSite(site)
        setCustomerInfo(site.customer)

        await router.replace({ name: 'confirmNearbySite' })
      }

      function getCustomerLogo(customer: Customer): string {
        return (
          customerLogoMap.get(customer.name ?? '') ??
          (customerLogoMap.get('default') as string)
        )
      }

      return {
        bypass,
        getCustomerLogo,
        isVisitor,
        loading,
        loadingMessage,
        noResult,
        radius,
        selectSite,
        showBypassOption,
        siteName,
        sites,
      }
    },
  })
</script>
